import { useState, useMemo } from "react";
import { motion } from "framer-motion";
import { Calculator, Airplane, Car, Lightning, ForkKnife, X, Leaf, ArrowRight } from "@phosphor-icons/react";
import { DonateModal } from "./DonateButton";

// Emission factors (kg CO2e)
const FACTORS = {
  flightShortHaul: 0.255,   // per passenger-km (<1500 km)
  flightLongHaul: 0.195,    // per passenger-km (>=1500 km, but more efficient per km)
  carPetrol: 0.171,         // per km (avg petrol)
  electricityKwh: 0.475,    // per kWh (global grid avg)
};

// Diet annual footprint (tons CO2e/year)
const DIET_TONS = {
  "high-meat": 3.3,
  "average": 2.5,
  "low-meat": 2.0,
  "vegetarian": 1.7,
  "vegan": 1.5,
};

// Offset price per ton CO2e (USD) — voluntary market avg
const OFFSET_USD_PER_TON = 20;

const DIET_OPTIONS = [
  { id: "high-meat", label: "High meat", desc: "Meat most days" },
  { id: "average", label: "Average", desc: "Mixed diet" },
  { id: "low-meat", label: "Low meat", desc: "Meat 1-2x/week" },
  { id: "vegetarian", label: "Vegetarian", desc: "No meat" },
  { id: "vegan", label: "Vegan", desc: "Plant-based" },
];

export default function CarbonCalculator() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid="open-carbon-calculator"
        className="inline-flex items-center gap-2 bg-stone-900 hover:bg-stone-800 text-white text-sm font-medium px-5 py-2.5 rounded-full transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg"
      >
        <Calculator size={16} weight="bold" /> Calculate My Footprint
      </button>
      {open && <CalculatorModal onClose={() => setOpen(false)} />}
    </>
  );
}

function CalculatorModal({ onClose }) {
  const [flightKm, setFlightKm] = useState(2000);
  const [carKm, setCarKm] = useState(8000);
  const [kwh, setKwh] = useState(300);
  const [diet, setDiet] = useState("average");
  const [showOffset, setShowOffset] = useState(false);

  const result = useMemo(() => {
    // Estimate split: assume 60% short-haul, 40% long-haul
    const flightCo2Kg = (flightKm * 0.6 * FACTORS.flightShortHaul) + (flightKm * 0.4 * FACTORS.flightLongHaul);
    const carCo2Kg = carKm * FACTORS.carPetrol;
    const electricCo2Kg = kwh * 12 * FACTORS.electricityKwh; // monthly -> yearly
    const dietCo2Kg = (DIET_TONS[diet] || 2.5) * 1000;
    const totalKg = flightCo2Kg + carCo2Kg + electricCo2Kg + dietCo2Kg;
    const tons = totalKg / 1000;
    const offsetUsd = Math.max(5, Math.round(tons * OFFSET_USD_PER_TON));
    return {
      flight: flightCo2Kg / 1000,
      car: carCo2Kg / 1000,
      electric: electricCo2Kg / 1000,
      diet: dietCo2Kg / 1000,
      tons,
      offsetUsd,
    };
  }, [flightKm, carKm, kwh, diet]);

  // Benchmark: 4t = sustainable, 8t = global avg, 16t = US avg
  const benchmark = result.tons < 4 ? "below" : result.tons < 8 ? "near-avg" : "above";
  const benchmarkLabel = {
    "below": { text: "Below sustainable target — well done!", color: "text-emerald-700 bg-emerald-50 border-emerald-200" },
    "near-avg": { text: "Around global average (~5t/yr).", color: "text-amber-700 bg-amber-50 border-amber-200" },
    "above": { text: "Above global average. Offsetting helps reverse this.", color: "text-rose-700 bg-rose-50 border-rose-200" },
  }[benchmark];

  if (showOffset) {
    return (
      <DonateModal
        onClose={onClose}
        initialAmount={result.offsetUsd}
        initialCurrency="USD"
        headline="Offset Your Footprint"
        description={`Your ${result.tons.toFixed(1)} tCO₂e footprint can be offset for about US$${result.offsetUsd}. Funds go to verified reforestation and satellite-MRV projects.`}
      />
    );
  }

  return (
    <div className="fixed inset-0 z-[80] bg-stone-900/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="carbon-calculator-modal">
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.25 }}
        className="bg-white rounded-2xl sm:rounded-3xl max-w-xl w-full overflow-hidden max-h-[92vh] overflow-y-auto"
      >
        <header className="flex items-center gap-3 p-4 sm:p-5 border-b border-stone-200 sticky top-0 bg-white z-10">
          <Calculator size={20} weight="duotone" className="text-emerald-700 shrink-0" />
          <div className="flex-1 min-w-0">
            <h2 className="font-heading text-base sm:text-lg font-semibold text-stone-900">Quick Carbon Footprint</h2>
            <p className="text-[11px] text-stone-500 hidden sm:block">Estimate your annual CO₂e — then offset in one tap.</p>
          </div>
          <button onClick={onClose} className="text-stone-500 hover:text-stone-900" data-testid="carbon-calc-close">
            <X size={18} weight="bold" />
          </button>
        </header>

        <div className="p-4 sm:p-6 space-y-4 sm:space-y-5">
          {/* Flights */}
          <Field
            icon={<Airplane size={16} weight="fill" className="text-sky-600" />}
            label="Flights per year (km)"
            hint="A round-trip London ↔ New York is ~11,000 km"
            value={flightKm}
            onChange={setFlightKm}
            testId="carbon-flight-km"
            max={100000}
            step={500}
          />

          {/* Car */}
          <Field
            icon={<Car size={16} weight="fill" className="text-stone-700" />}
            label="Personal car driving per year (km)"
            hint="Avg commuter: ~12,000 km/year"
            value={carKm}
            onChange={setCarKm}
            testId="carbon-car-km"
            max={50000}
            step={500}
          />

          {/* Electricity */}
          <Field
            icon={<Lightning size={16} weight="fill" className="text-amber-500" />}
            label="Home electricity per month (kWh)"
            hint="Typical household: ~300-900 kWh/month"
            value={kwh}
            onChange={setKwh}
            testId="carbon-kwh"
            max={3000}
            step={50}
          />

          {/* Diet */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-stone-700 mb-2">
              <ForkKnife size={16} weight="fill" className="text-rose-500" />
              Diet
            </label>
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-1.5">
              {DIET_OPTIONS.map((opt) => (
                <button
                  key={opt.id}
                  onClick={() => setDiet(opt.id)}
                  data-testid={`carbon-diet-${opt.id}`}
                  className={`text-[11px] py-2 px-1 rounded-lg border transition-colors text-center ${
                    diet === opt.id
                      ? "bg-emerald-700 border-emerald-800 text-white"
                      : "border-stone-200 text-stone-600 hover:border-emerald-400 hover:text-emerald-700"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Result */}
          <div className="border-t border-stone-200 pt-5">
            <div className="bg-stone-50 rounded-2xl p-5">
              <p className="text-[11px] uppercase tracking-wider text-stone-400 mb-1">Estimated annual footprint</p>
              <div className="flex items-baseline gap-2 mb-3">
                <span className="font-heading text-4xl font-light text-stone-900" data-testid="carbon-total-tons">
                  {result.tons.toFixed(1)}
                </span>
                <span className="text-sm text-stone-500">tons CO₂e / year</span>
              </div>
              <p className={`text-xs border rounded-lg px-3 py-2 mb-4 ${benchmarkLabel.color}`} data-testid="carbon-benchmark">
                {benchmarkLabel.text}
              </p>

              {/* Breakdown */}
              <div className="space-y-1.5 mb-4">
                <Row label="Flights" value={result.flight} color="bg-sky-500" total={result.tons} />
                <Row label="Car" value={result.car} color="bg-stone-700" total={result.tons} />
                <Row label="Electricity" value={result.electric} color="bg-amber-500" total={result.tons} />
                <Row label="Diet" value={result.diet} color="bg-rose-500" total={result.tons} />
              </div>

              <button
                onClick={() => setShowOffset(true)}
                data-testid="carbon-offset-now"
                className="w-full bg-emerald-700 hover:bg-emerald-800 text-white text-sm font-medium py-3 rounded-xl transition-colors flex items-center justify-center gap-2"
              >
                <Leaf size={16} weight="fill" /> Offset for US${result.offsetUsd} <ArrowRight size={14} weight="bold" />
              </button>
              <p className="text-[10px] text-stone-400 text-center mt-2">
                Estimates use IPCC/IEA standard emission factors. Offset price: US${OFFSET_USD_PER_TON}/tCO₂e (voluntary market avg).
              </p>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

function Field({ icon, label, hint, value, onChange, testId, max, step }) {
  return (
    <div>
      <label className="flex items-center gap-2 text-sm font-medium text-stone-700 mb-1">
        {icon} {label}
      </label>
      <div className="flex items-center gap-3">
        <input
          type="range"
          min={0}
          max={max}
          step={step}
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          className="flex-1 accent-emerald-700"
          data-testid={`${testId}-range`}
        />
        <input
          type="number"
          min={0}
          step={step}
          value={value}
          onChange={(e) => onChange(Math.max(0, Number(e.target.value) || 0))}
          className="w-24 px-2 py-1.5 border border-stone-200 rounded-lg text-sm text-right focus:outline-none focus:border-emerald-500"
          data-testid={testId}
        />
      </div>
      <p className="text-[10px] text-stone-400 mt-1">{hint}</p>
    </div>
  );
}

function Row({ label, value, color, total }) {
  const pct = total > 0 ? Math.min(100, (value / total) * 100) : 0;
  return (
    <div className="flex items-center gap-2 text-[11px]">
      <span className="w-20 text-stone-500">{label}</span>
      <div className="flex-1 h-2 bg-stone-200 rounded-full overflow-hidden">
        <div className={`h-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="w-14 text-right font-medium text-stone-700">{value.toFixed(1)} t</span>
    </div>
  );
}
